// Unified replay-event subscription. Tauri mode wraps the backend events; web
// mode has no event channel (the webserver's emit_event is a no-op), so it polls
// get_replay_status (1s) and synthesizes the same transitions. Returns an
// unsubscribe fn. Used so the -live web tab refreshes views/graphs as replay
// advances and when a new window finishes loading.
import * as replayControls from "./replayControls";

export type ReplayEventName =
  | "replay-started"
  | "replay-finished"
  | "replay-data-loaded"
  | "replay-tick";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export function onReplayEvent(name: ReplayEventName, cb: () => void): () => void {
  if (isTauri()) {
    let off: (() => void) | undefined;
    let cancelled = false;
    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const unlisten = await listen(name, () => cb());
      if (cancelled) unlisten();
      else off = unlisten;
    });
    return () => { cancelled = true; off?.(); };
  }

  // Web: poll and derive transitions. Each subscription polls independently (1s),
  // which is cheap and matches the existing toolbar poll cadence.
  let prevActive = false;
  let prevLoadedEnd = 0;
  let first = true;
  let stopped = false;
  const tick = async () => {
    const s = await replayControls.getReplayStatus();
    if (stopped || !s) return;
    if (name === "replay-started" && s.active && !prevActive) cb();
    if (name === "replay-finished" && !s.active && prevActive) cb();
    if (name === "replay-tick" && s.active) cb();
    // Fire on a changed, non-zero loaded window — but not on the very first poll
    // (a window already loaded at mount is not a fresh load).
    if (
      name === "replay-data-loaded" &&
      !first &&
      s.loaded_end !== prevLoadedEnd &&
      s.loaded_end !== 0
    ) {
      cb();
    }
    prevActive = s.active;
    prevLoadedEnd = s.loaded_end;
    first = false;
  };
  const id = setInterval(tick, 1000);
  return () => { stopped = true; clearInterval(id); };
}
